import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemButton,
  Typography,
  Box,
  Chip,
  InputAdornment,
  Divider,
  Fade,
  Paper,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Search as SearchIcon,
  Close as CloseIcon,
  Calculate as CalculateIcon,
  Vaccines as VaccinesIcon,
  PhotoCamera as PhotoCameraIcon,
  LocalHospital as HospitalIcon,
  Description as DescriptionIcon,
  Psychology as PsychologyIcon,
  TrendingUp as TrendingUpIcon,
  ChildCare as ChildCareIcon,
  Schedule as ScheduleIcon,
  FilterList as FilterListIcon,
} from '@mui/icons-material';

interface SearchSection {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  path: string;
  category: string;
  keywords: string[];
  relatedSections: string[];
}

interface SectionSearchProps {
  open: boolean;
  onClose: () => void;
  onNavigate: (path: string) => void;
}

const SectionSearch: React.FC<SectionSearchProps> = ({
  open,
  onClose,
  onNavigate,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredSections, setFilteredSections] = useState<SearchSection[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');

  const sections: SearchSection[] = [
    {
      id: 'calculator',
      title: 'Калькулятор дозировок',
      description: 'Расчет дозировок лекарств для детей по возрасту и весу',
      icon: <CalculateIcon />,
      path: '/calculator',
      category: 'tools',
      keywords: ['калькулятор', 'дозировка', 'лекарства', 'дети', 'вес', 'возраст', 'парацетамол', 'ибупрофен', 'амоксициллин'],
      relatedSections: ['children-up-to-year', 'emergency-care']
    },
    {
      id: 'vaccination',
      title: 'Календарь вакцинации',
      description: 'Национальный и идеальный календари вакцинации детей',
      icon: <VaccinesIcon />,
      path: '/vaccination',
      category: 'prevention',
      keywords: ['вакцинация', 'прививки', 'календарь', 'бцж', 'акдс', 'корь', 'краснуха', 'паротит'],
      relatedSections: ['children-up-to-year', 'orders']
    },
    {
      id: 'atlas',
      title: 'Атлас сыпей',
      description: 'Визуальная диагностика инфекций и кожных высыпаний',
      icon: <PhotoCameraIcon />,
      path: '/atlas',
      category: 'diagnostics',
      keywords: ['атлас', 'сыпь', 'инфекции', 'кожа', 'диагностика', 'ветрянка', 'краснуха', 'корь'],
      relatedSections: ['emergency-care', 'children-up-to-year']
    },
    {
      id: 'emergency-care',
      title: 'Неотложная помощь',
      description: 'Алгоритмы оказания неотложной помощи детям',
      icon: <HospitalIcon />,
      path: '/emergency-care',
      category: 'emergency',
      keywords: ['неотложная', 'помощь', 'экстренная', 'алгоритмы', 'реанимация', 'кислород', 'ларингит'],
      relatedSections: ['calculator', 'atlas', 'ai-assistant']
    },
    {
      id: 'children-up-to-year',
      title: 'Дети до года',
      description: 'Особенности развития, питания и ухода за детьми до года',
      icon: <ChildCareIcon />,
      path: '/children-up-to-year',
      category: 'development',
      keywords: ['дети', 'до года', 'новорожденные', 'грудное вскармливание', 'прикорм', 'навыки', 'развитие'],
      relatedSections: ['calculator', 'vaccination', 'atlas']
    },
    {
      id: 'growth-charts',
      title: 'Графики роста и веса',
      description: 'Нормы роста и веса по ВОЗ и CDC с возможностью отметки измерений',
      icon: <TrendingUpIcon />,
      path: '/growth-charts',
      category: 'development',
      keywords: ['рост', 'вес', 'графики', 'нормы', 'воз', 'cdc', 'измерения', 'процентили'],
      relatedSections: ['children-up-to-year', 'calculator']
    },
    {
      id: 'orders',
      title: 'Приказы и нормативы',
      description: 'Профилактические осмотры согласно приказу 211-н',
      icon: <DescriptionIcon />,
      path: '/orders',
      category: 'regulations',
      keywords: ['приказы', 'нормативы', 'осмотры', '211-н', 'профилактические', 'специалисты', 'обследования'],
      relatedSections: ['vaccination', 'children-up-to-year']
    },
    {
      id: 'ai-assistant',
      title: 'ИИ-помощник',
      description: 'Анализ медицинских данных с помощью искусственного интеллекта',
      icon: <PsychologyIcon />,
      path: '/ai-assistant',
      category: 'tools',
      keywords: ['ии', 'помощник', 'анализ', 'искусственный интеллект', 'данные', 'кровь', 'моча', 'биохимия'],
      relatedSections: ['calculator', 'emergency-care']
    }
  ];

  const categories = [
    { id: 'all', label: 'Все разделы', count: sections.length },
    { id: 'tools', label: 'Инструменты', count: sections.filter(s => s.category === 'tools').length },
    { id: 'prevention', label: 'Профилактика', count: sections.filter(s => s.category === 'prevention').length },
    { id: 'diagnostics', label: 'Диагностика', count: sections.filter(s => s.category === 'diagnostics').length },
    { id: 'emergency', label: 'Неотложная помощь', count: sections.filter(s => s.category === 'emergency').length },
    { id: 'development', label: 'Развитие', count: sections.filter(s => s.category === 'development').length },
    { id: 'regulations', label: 'Нормативы', count: sections.filter(s => s.category === 'regulations').length },
  ];

  useEffect(() => {
    let filtered = sections;

    // Фильтр по категории
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(section => section.category === selectedCategory);
    }

    // Фильтр по поисковому запросу
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(section =>
        section.title.toLowerCase().includes(query) ||
        section.description.toLowerCase().includes(query) ||
        section.keywords.some(keyword => keyword.toLowerCase().includes(query))
      );
    }

    setFilteredSections(filtered);
  }, [searchQuery, selectedCategory]);

  const handleSectionClick = (path: string) => {
    onNavigate(path);
    onClose();
  };

  const getCategoryColor = (category: string) => {
    const colors: { [key: string]: string } = {
      tools: '#007bff',
      prevention: '#28a745',
      diagnostics: '#ffc107',
      emergency: '#dc3545',
      development: '#17a2b8',
      regulations: '#6f42c1',
    };
    return colors[category] || '#6c757d';
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          maxHeight: '80vh',
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        pb: 1
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <SearchIcon sx={{ mr: 1, color: '#007bff' }} />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Поиск по разделам
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        {/* Поисковая строка */}
        <Box sx={{ p: 2, pb: 1 }}>
          <TextField
            fullWidth
            placeholder="Введите название раздела или ключевые слова..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: '#6c757d' }} />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
              }
            }}
          />
        </Box>

        {/* Категории */}
        <Box sx={{ px: 2, pb: 2 }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {categories.map((category) => (
              <Chip
                key={category.id}
                label={`${category.label} (${category.count})`}
                onClick={() => setSelectedCategory(category.id)}
                variant={selectedCategory === category.id ? 'filled' : 'outlined'}
                size="small"
                sx={{
                  backgroundColor: selectedCategory === category.id ? '#007bff' : 'transparent',
                  color: selectedCategory === category.id ? 'white' : '#007bff',
                  borderColor: '#007bff',
                  '&:hover': {
                    backgroundColor: selectedCategory === category.id ? '#0056b3' : 'rgba(0, 123, 255, 0.1)',
                  },
                }}
              />
            ))}
          </Box>
        </Box>

        <Divider />

        {/* Результаты поиска */}
        <Box sx={{ maxHeight: '400px', overflow: 'auto' }}>
          {filteredSections.length === 0 ? (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                {searchQuery ? 'Разделы не найдены' : 'Выберите категорию или введите поисковый запрос'}
              </Typography>
            </Box>
          ) : (
            <List sx={{ p: 0 }}>
              {filteredSections.map((section, index) => (
                <Fade in={true} timeout={300 + index * 100} key={section.id}>
                  <ListItem disablePadding>
                    <ListItemButton
                      onClick={() => handleSectionClick(section.path)}
                      sx={{
                        p: 2,
                        '&:hover': {
                          backgroundColor: 'rgba(0, 123, 255, 0.05)',
                        },
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 40, color: getCategoryColor(section.category) }}>
                        {section.icon}
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                            {section.title}
                          </Typography>
                        }
                        secondary={
                          <Box>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                              {section.description}
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                              {section.keywords.slice(0, 3).map((keyword, idx) => (
                                <Chip
                                  key={idx}
                                  label={keyword}
                                  size="small"
                                  variant="outlined"
                                  sx={{
                                    fontSize: '0.7rem',
                                    height: 20,
                                    borderColor: '#e9ecef',
                                    color: '#6c757d',
                                  }}
                                />
                              ))}
                              {section.keywords.length > 3 && (
                                <Typography variant="caption" color="text.secondary">
                                  +{section.keywords.length - 3} еще
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        }
                      />
                    </ListItemButton>
                  </ListItem>
                </Fade>
              ))}
            </List>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2, pt: 1 }}>
        <Button onClick={onClose} variant="outlined">
          Закрыть
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SectionSearch;
